import type { RefCallback, ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/modern/utils";
import type { FileViewerMode } from "../types";
import { MetaRail } from "./MetaRail";
import { PromotePanel } from "./promote/PromotePanel";
import { ExtractionPanel } from "./extraction/ExtractionPanel";
import { SummarySharePanel } from "./SummarySharePanel";
import { AdjustPointsPanel } from "./AdjustPointsPanel";

type RailMode = Exclude<FileViewerMode, "annotate">;

interface FileDetailRailProps {
  mode: RailMode;
  analyzeDockRef: RefCallback<HTMLDivElement>;
  /** Kept for callers; panels close themselves back to Details. */
  onBackToDetails: () => void;
}

/**
 * Soft bubbly card — same footprint for every surface so mode switches never shift layout.
 * Width tracks the shell as a percentage so focused near-fullscreen stays readable on large displays.
 */
export const FILE_DETAIL_RAIL_SHELL_CLS = cn(
  "relative flex h-full min-h-0 w-[30%] min-w-[26rem] shrink-0 flex-col self-stretch",
  "overflow-hidden rounded-[2rem] bg-card p-5",
  "border border-border/50 shadow-sm",
);

function RailSurface({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">{children}</div>
  );
}

/**
 * Right rail card: Details / Summary & Share / Promote / Extract / Analyze / Adjust Points.
 * One fixed shell; surfaces crossfade in-place (no width/height change).
 */
export function FileDetailRail({ mode, analyzeDockRef }: FileDetailRailProps) {
  return (
    <aside className={FILE_DETAIL_RAIL_SHELL_CLS} aria-label="File panel">
      <div className="relative min-h-0 flex-1">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={mode}
            className="absolute inset-0 flex min-h-0 flex-col overflow-hidden"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          >
            {mode === "default" ? (
              <RailSurface>
                <MetaRail />
              </RailSurface>
            ) : null}
            {mode === "summaryShare" ? (
              <RailSurface>
                <SummarySharePanel />
              </RailSurface>
            ) : null}
            {mode === "promote" ? (
              <RailSurface>
                <PromotePanel />
              </RailSurface>
            ) : null}
            {mode === "extract" ? (
              <RailSurface>
                <ExtractionPanel />
              </RailSurface>
            ) : null}
            {mode === "analyze" ? (
              <RailSurface>
                <div ref={analyzeDockRef} className="flex h-full min-h-0 flex-col" />
              </RailSurface>
            ) : null}
            {mode === "adjustPoints" ? (
              <RailSurface>
                <AdjustPointsPanel />
              </RailSurface>
            ) : null}
          </motion.div>
        </AnimatePresence>
      </div>
    </aside>
  );
}
